import { useEffect, useRef } from "preact/hooks";
import { useStore } from "../../state/useStore.ts";
import { K } from "../../state/keys.ts";
import { resultView, achievementToast } from "../../domain/resultView.ts";
import type { PracticeOutcome } from "../../domain/resultView.ts";
import * as practice from "../../legacy/practice.js";
import * as toast from "../../legacy/toast.js";
import { guarded } from "../../legacy/practiceView.js";
import { challenge } from "../../services/share.ts";

/** How a practice ended: the score, where the XP came from, and what to do next. */
/** The result sits under the board, so starting over from it starts with the board in view again. */
const toTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

export function PracticeResult() {
    const status = useStore(K.practice.status);
    const result = useStore(K.practice.result) as PracticeOutcome | null;
    const line = useStore(K.practice.line);
    const isLoggedIn = !!useStore(K.user.currentUser);
    const anonymousLimit = useStore(K.progress.anonymousLimit);
    // The line is today's opening: the way on is the day, once it has been played enough (RoutineBar)
    const routine = useStore(K.studyLine.routine);

    // The notifications belong to the practice that just ended, not to every render of it.
    const announced = useRef<PracticeOutcome | null>(null);
    useEffect(() => {
        if (status !== "finished" || !result || announced.current === result) return;
        announced.current = result;

        const achievement = achievementToast(result.rewards?.achievements || []);
        if (achievement) {
            toast.showToast({ ...achievement, type: "success", duration: result.rewards!.achievements!.length > 1 ? 7000 : 6000 });
        }
        if (result.counted && !isLoggedIn && anonymousLimit > 0 && result.practiceNumber >= anonymousLimit) {
            toast.showToast({
                title: "Nice work", type: "info", duration: 8000,
                message: "Create a free account to keep your progress and continue practicing.",
            });
        }
    }, [status, result, isLoggedIn, anonymousLimit]);

    if (status !== "finished" || !result) return null;

    const view = resultView(result, { isLoggedIn, lineSource: line?.source ?? null });
    // The score, the stars and what it earned: where each point came from read as noise (2026-11-01), so it is gone
    const xpTotal = view.xpLines.find((xp) => xp.total)?.xp ?? null;

    return (
        <div class="scoresheet__result">
            <div class="scoresheet__score">
                <span class="scoresheet__accuracy">{view.accuracy}</span>
                <span class="scoresheet__score-side">
                    <span class="scoresheet__stars" aria-label={view.starsLabel}>{view.stars}</span>
                    {xpTotal !== null && <span class="scoresheet__counts">+{xpTotal} XP</span>}
                </span>
            </div>

            {view.notes.length > 0 && <p class="scoresheet__notes">{view.notes.join(" ")}</p>}

            {view.slip && (
                <div class="scoresheet__slip">
                    <span>
                        Biggest slip: <strong>{view.slip.label}</strong>
                        {view.slip.bestSan ? `, ${view.slip.bestSan} was better` : ""}.
                    </span>
                    <button type="button" class="btn btn-ghost btn-sm" onClick={() => { toTop(); practice.retryFrom(view.slip!.ply); }}>
                        Retry from there
                    </button>
                </div>
            )}

            {routine && (
                <p class="scoresheet__routine">
                    {routine.canContinue
                        ? `Today's opening is done: ${routine.played} of ${routine.needed} plays.`
                        : `Today's opening: ${routine.played} of ${routine.needed} plays. Play it again to go on.`}
                </p>
            )}

            <div class="scoresheet__result-actions">
                {routine?.canContinue && (
                    <a class="btn btn-primary" href={routine.back}>Continue today's training</a>
                )}
                <button type="button" class={routine?.canContinue ? "btn btn-ghost" : "btn btn-primary"}
                        onClick={() => { toTop(); guarded(() => practice.restart()); }}>
                    Practice again
                </button>
                <button type="button" class="btn btn-ghost" onClick={() => { toTop(); guarded(() => practice.startAnotherLine()); }}>
                    Try random line
                </button>
                {view.canChallenge && (
                    <button type="button" class="btn btn-ghost"
                            onClick={() => challenge(result.accuracy!, line!)}>
                        Challenge a friend
                    </button>
                )}
            </div>
        </div>
    );
}
